import React, { useState, useEffect, useMemo, useId } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useApi from "../../../CustomHooks/useApi";
import { AIR_MONO, AirFontImport, Readout } from "./airTheme";

// ── Design tokens (matches FlightBooking.js) ────────────────────────
const C = {
  navy:   "#0B1A35",
  navyMd: "#162C5C",
  sky:    "#0EA5E9",
  bg:     "#F0F4FA",
  card:   "#FFFFFF",
  border: "#E2EAF4",
  muted:  "#64748B",
  label:  "#94A3B8",
  green:  "#10B981",
  amber:  "#F59E0B",
  red:    "#EF4444",
};

// ── Icon system ──────────────────────────────────────────────────────
const Icon = ({ d, size = 16, fill = "none", strokeWidth = "2", ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke="currentColor"
    strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    {d}
  </svg>
);
const IcArrowLeft = (p) => <Icon size={p.size} d={<><path d="M19 12H5"/><polyline points="12 19 5 12 12 5"/></>} />;
const IcCheck     = (p) => <Icon size={p.size || 11} strokeWidth={p.strokeWidth || "2.5"} d={<polyline points="20 6 9 17 4 12"/>} />;
const IcUser       = (p) => <Icon size={p.size} d={<><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></>} />;
const IcBriefcase  = (p) => <Icon size={p.size} d={<><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></>} />;
const IcArmchair   = (p) => <Icon size={p.size} d={<><path d="M5 11V6a2 2 0 012-2h10a2 2 0 012 2v5"/><path d="M4 11h16v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5z"/><path d="M6 18v2M18 18v2"/></>} />;
const IcInfo       = (p) => <Icon size={p.size} d={<><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></>} />;

// ── Airline logo — same CDN + deterministic fallback badge used on the
//    search results page (FlightSearch.js), reused here so the airline
//    reads consistently across the whole Air module. ─────────────────
const IATA_TO_ICAO = {
  "6E": "IGO", AI: "AIC", UK: "VTI", SG: "SEJ", G8: "GOW", I5: "AXB", IX: "AXB",
  QP: "AKJ", "9I": "LLR",
  EK: "UAE", QR: "QTR", SQ: "SIA", TG: "THA", MH: "MAS", CX: "CPA", BA: "BAW",
  LH: "DLH", AF: "AFR", KL: "KLM", TK: "THY", EY: "ETD", SV: "SVA", UL: "ALK",
  OS: "AUA", VN: "HVN", JL: "JAL", NH: "ANA", KE: "KAL", CI: "CAL", BR: "EVA",
  PR: "PAL", GA: "GIA", VA: "VOZ", QF: "QFA", NZ: "ANZ", EI: "EIN", LX: "SWR",
  IB: "IBE", AZ: "ITY", SU: "AFL", FZ: "UAE", ET: "ETH",
};
const airlineLogoUrl = (iataCode) => {
  const icao = IATA_TO_ICAO[(iataCode || "").toUpperCase()];
  return icao ? `https://raw.githubusercontent.com/Jxck-S/airline-logos/main/flightaware_logos/${icao}.png` : null;
};
const AIRLINE_PALETTE = [
  { bg: "#EFF6FF", fg: "#3B82F6" },
  { bg: "#F0FDF4", fg: "#059669" },
  { bg: "#FDF4FF", fg: "#A21CAF" },
  { bg: "#FFF7ED", fg: "#C2410C" },
  { bg: "#FEF2F2", fg: "#DC2626" },
  { bg: "#ECFEFF", fg: "#0891B2" },
];
const airlineAccent = (key) => {
  const s = (key || "").toUpperCase();
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return AIRLINE_PALETTE[hash % AIRLINE_PALETTE.length];
};

// ── Date/time formatting — same helpers as FlightSearch.js, so a time
//    reads identically whether it's on the results page or here. ────
const fmtTime = (dt) => {
  if (!dt) return "--:--";
  // ISO ("2026-08-08T01:30:00"): time starts right after the T. Non-ISO
  // ("08/08/2026 01:30", exactly 16 chars) used to fall through the old
  // length>16 check and render as-is — showing the whole date+time as the
  // "time". Split on whitespace instead so either shape works.
  if (dt.includes("T")) return dt.slice(11, 16);
  const parts = dt.trim().split(/\s+/);
  return parts.length > 1 ? parts[1].slice(0, 5) : dt;
};
const fmtCardDate = (dt) => {
  if (!dt) return "";
  const d = new Date(dt);
  return isNaN(d) ? "" : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// ── Pax list — same numbering scheme as FlightBooking.buildPaxList ──
const buildPaxList = ({ adults = 1, children = 0, infants = 0 } = {}) => {
  const list = [];
  let id = 1;
  for (let i = 0; i < adults;   i++) list.push({ paxId: id++, paxType: 0, label: "Adult" });
  for (let i = 0; i < children; i++) list.push({ paxId: id++, paxType: 1, label: "Child" });
  for (let i = 0; i < infants;  i++) list.push({ paxId: id++, paxType: 2, label: "Infant" });
  return list;
};

// Parse a seat code like "1A" -> { row: "1", col: "A" }. Falls back gracefully.
const parseSeatCode = (code) => {
  const m = /^(\d+)\s*([A-Za-z])$/.exec((code || "").trim());
  return m ? { row: m[1], col: m[2].toUpperCase() } : { row: code || "?", col: "" };
};

// ── Nextra SSR/seat-map normalization ────────────────────────────────
// Nextra's /get-ssr and /seat-map responses are structurally unrelated to WeFlyGo's — flatten
// them into the exact SSRDetails/AirSeatMaps shape this page already renders (SeatGrid,
// Fuselage, AddonGroup etc. below all stay unchanged), so the SAME seat map / add-on picker UI
// used for Air1 renders Air2 data too. Confirmed against live Nextra UAT calls:
// - /seat-map already marks every seat explicitly "Open"/"Closed" (no implied-occupied gaps to
//   infer, unlike WeFlyGo) and gives a real "1A"-style seatdesignator.
// - /get-ssr returns a flat, ungrouped list (baggage/meal/specialssr) with DepStation/ArrStation
//   identifying which physical segment each item belongs to.
// Each normalized item also carries a `_nextraRaw` passthrough (ssrid/ssrcode/ssrtype/etc) that
// the render components below ignore, but that FlightSSRSeat's own booking-payload builder
// further down uses to reconstruct Nextra's own NextraSSRRow shape for the real /book call.
const NEXTRA_SSR_TYPE_LABELS = { baggage: "Baggage", meal: "Meal", specialssr: "Other" };

const normalizeNextraSeatMap = (seatMapResponse, jtype) => {
  const legs = seatMapResponse?.ApiStatus?.Result?.list || [];
  return {
    Seat_Segments: legs.map((leg) => {
      const byRow = {};
      (leg.seatrows || []).forEach((sr) => {
        const rowNum = sr.rownumber || parseSeatCode(sr.seatdesignator).row;
        (byRow[rowNum] = byRow[rowNum] || []).push({
          SSR_Key: sr.seatgroup || sr.seatdesignator,
          SSR_TypeName: sr.seatdesignator,
          SSR_TypeDesc: sr.seatdesignator,
          Total_Amount: sr.seatprice || 0,
          SSR_Status: sr.seatstatus === "Open" ? 1 : 3, // 1=AVAILABLE, 3=BOOKED (unavailable)
          _nextraRaw: {
            ssrid: sr.seatgroup || sr.seatdesignator, ssrcode: sr.seatdesignator,
            ssrname: `Seat ${sr.seatdesignator}`, ssrtype: "seat",
            ssr_chargeableamount: sr.seatprice || 0,
            carrier: leg.carrier, flightNumber: leg.flightnumber,
            origin: leg.origin, destination: leg.destination, jtype,
          },
        });
      });
      return { Seat_Row: Object.values(byRow).map((seats) => ({ Seat_Details: seats })) };
    }),
  };
};

// legSegments = repricedFlights[legIdx].Segments — used to match each SSR item's DepStation/
// ArrStation against the right physical segment (WeFlyGo's Segment_Id equivalent) so connecting
// itineraries still bucket each add-on under the correct leg of the journey.
const normalizeNextraSSR = (ssrResponse, legSegments, jtype) => {
  const items = ssrResponse?.ApiStatus?.Result?.list?.CarrierSSR || [];
  return items.map((it) => {
    const segIdx = (legSegments || []).findIndex(
      (s) => s.Origin === it.DepStation && s.Destination === it.ArrStation
    );
    return {
      SSR_Key: it.ssrid,
      SSR_Type: 1, // anything !== 3 (WeFlyGo's SEAT sentinel) — Nextra's addon list never contains seats
      SSR_TypeName: NEXTRA_SSR_TYPE_LABELS[it.ssrtype] || "Other",
      SSR_TypeDesc: it.ssrname,
      Total_Amount: it.chargeableamount || 0,
      Segment_Wise: true,
      Segment_Id: String(segIdx >= 0 ? segIdx : 0),
      _nextraRaw: {
        ssrid: it.ssrid, ssrcode: it.ssrcode, ssrname: it.ssrname, ssrtype: it.ssrtype,
        ssr_chargeableamount: it.chargeableamount || 0,
        carrier: it.carrier, flightNumber: it.FlightNumber,
        origin: it.DepStation, destination: it.ArrStation, jtype,
      },
    };
  });
};

// ── Flight info card — airline, times, duration/stops, date. All fields
//    are real data already used elsewhere in this module (FlightSearch.js,
//    FlightBooking.js) — nothing here is invented. ────────────────────
const FlightInfoCard = ({ f, legLabel }) => {
  const [logoFailed, setLogoFailed] = useState(false);
  const segs = f.Segments || [];
  const first = segs[0];
  const last = segs[segs.length - 1];
  const stops = Math.max(segs.length - 1, 0);
  const airlineCode = f.Airline_Code || first?.Airline_Code || "";
  const airlineName = first?.Airline_Name || airlineCode || "Airline";
  const initials = (airlineCode || airlineName).replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase() || "FL";
  const accent = airlineAccent(airlineCode || airlineName);
  const logoUrl = airlineLogoUrl(airlineCode);
  // f.Flight_Numbers is a raw WeFlyGo field that, for connecting itineraries, can come back
  // as more than comma-joined flight numbers (e.g. with a segment's date/time jammed in too) —
  // build the display from each segment's own clean Flight_Number instead of parsing that string.
  const flightNumbers = segs.map((s) => s.Flight_Number).filter(Boolean).join(" · ");

  return (
    <div style={{ background: C.card, borderRadius: 16, border: `1px solid ${C.border}`, boxShadow: "0 1px 4px rgba(0,0,0,0.04)", padding: 18, marginBottom: 14 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: C.label, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>
        {legLabel}
      </div>
      <div className="air-flight-info-row" style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div className="air-flight-info-airline" style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          {logoUrl && !logoFailed ? (
            <img
              src={logoUrl} alt={airlineName} onError={() => setLogoFailed(true)}
              style={{ width: 40, height: 40, borderRadius: 10, objectFit: "contain", background: accent.bg, border: `1px solid ${C.border}`, padding: 5, flexShrink: 0, boxSizing: "border-box" }}
            />
          ) : (
            <div style={{ width: 40, height: 40, borderRadius: 10, background: accent.bg, color: accent.fg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, flexShrink: 0 }}>
              {initials}
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: C.navy, whiteSpace: "nowrap" }}>{airlineName}</div>
            <Readout size={11} weight={500} color={C.label}>
              {flightNumbers}{first?.Aircraft_Type ? ` · ${first.Aircraft_Type}` : ""}
            </Readout>
          </div>
        </div>

        <div className="air-flight-info-route" style={{ flex: 1, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: "16px 24px" }}>
          <div style={{ textAlign: "center" }}>
            <Readout size={22} weight={700} color={C.navy}>{fmtTime(first?.Departure_DateTime)}</Readout>
            <div style={{ fontSize: 11, color: C.label, marginTop: 2 }}>{fmtCardDate(first?.Departure_DateTime)}</div>
            <div style={{ marginTop: 1 }}><Readout size={12} weight={700} color={C.muted}>{first?.Origin || "---"}</Readout></div>
          </div>
          <div style={{ textAlign: "center", minWidth: 130 }}>
            <div style={{ fontSize: 10, color: C.label, marginBottom: 5 }}>{first?.Duration || ""}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <div style={{ flex: 1, borderTop: `1.5px dashed ${C.border}` }} />
              <span style={{ fontSize: 12, color: C.label }}>✈</span>
              <div style={{ flex: 1, borderTop: `1.5px dashed ${C.border}` }} />
            </div>
            <div style={{ marginTop: 5 }}>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 8,
                background: stops === 0 ? "#D1FAE5" : "#FEF3C7",
                color: stops === 0 ? "#059669" : "#B45309",
              }}>
                {stops === 0 ? "Direct" : `${stops} stop${stops > 1 ? "s" : ""}`}
              </span>
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <Readout size={22} weight={700} color={C.navy}>{fmtTime(last?.Arrival_DateTime)}</Readout>
            <div style={{ fontSize: 11, color: C.label, marginTop: 2 }}>{fmtCardDate(last?.Arrival_DateTime)}</div>
            <div style={{ marginTop: 1 }}><Readout size={12} weight={700} color={C.muted}>{last?.Destination || "---"}</Readout></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── One seat button — bordered pill, code + price/Free stacked. An
//    "occupied" seat (a real row/column position with no sellable
//    inventory for it — see Fuselage below) renders as a plain disabled
//    grey block instead of an invisible spacer. ─────────────────────
const SEAT_SIZE = 34;
const SeatButton = ({ seat, code, paxId, groupKey, selected, takenSeatKeys, onSelect }) => {
  if (!seat) {
    return (
      <div
        title={`Seat ${code} · Occupied`}
        style={{
          width: SEAT_SIZE, height: SEAT_SIZE, borderRadius: "8px 8px 10px 10px",
          background: "#E7EAF0", color: "#9AA5B8",
          fontFamily: AIR_MONO, fontSize: 9, fontWeight: 700,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}
      >
        {code}
      </div>
    );
  }
  const displayCode = seat.SSR_TypeName || code;
  const isSelected = !!seat.SSR_Key && selected === seat.SSR_Key;
  // A seat another passenger on this same booking already picked — each of the N passengers
  // needs their OWN seat, so this must block re-selection rather than let two passengers end
  // up assigned to the same physical seat.
  const isTakenByOther = !isSelected && !!seat.SSR_Key && takenSeatKeys?.has(seat.SSR_Key);
  const isFree = !seat.Total_Amount;
  // Per WeFlyGo's Air_GetSeatMap docs, SSR_Status on a seat is an enum: 1=AVAILABLE,
  // 2=BLOCKED, 3=BOOKED (0=ISLE is filtered out before this ever renders as a seat — see
  // Fuselage). We were only ever checking Total_Amount to decide selectability, so a seat
  // WeFlyGo already marked BLOCKED/BOOKED could still show as pickable.
  const isUnavailable = !isSelected && (seat.SSR_Status === 2 || seat.SSR_Status === 3);

  // BLOCKED/BOOKED is airline-side unavailability — the same real-world meaning as the
  // "Occupied" (missing-from-row) state, so it gets that solid style. "Taken by another
  // passenger" is a different, more temporary thing (specific to this booking, would free
  // up if that passenger picked a different seat), so it keeps its own dashed style.
  if (isUnavailable) {
    return (
      <div
        title={`Seat ${displayCode} · ${seat.SSR_Status === 3 ? "Already booked" : "Blocked"}`}
        style={{
          width: SEAT_SIZE, height: SEAT_SIZE, borderRadius: "8px 8px 10px 10px",
          background: "#E7EAF0", color: "#9AA5B8",
          fontFamily: AIR_MONO, fontSize: 9, fontWeight: 700,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}
      >
        {displayCode}
      </div>
    );
  }
  if (isTakenByOther) {
    return (
      <div
        title={`Seat ${displayCode} · Selected for another passenger`}
        style={{
          width: SEAT_SIZE, height: SEAT_SIZE, borderRadius: "8px 8px 10px 10px",
          border: `1.5px dashed ${C.border}`, background: "#F1F5F9", color: C.muted,
          fontFamily: AIR_MONO, fontSize: 9, fontWeight: 700,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}
      >
        {displayCode}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onSelect(paxId, groupKey, seat.SSR_Key)}
      title={`Seat ${displayCode} · ${isFree ? "Free" : `₹${seat.Total_Amount}`}`}
      style={{
        width: SEAT_SIZE, height: SEAT_SIZE, borderRadius: "8px 8px 10px 10px",
        border: `1.5px solid ${isSelected ? C.sky : isFree ? C.border : C.amber}`,
        background: isSelected ? C.sky : "#fff",
        color: isSelected ? "#fff" : C.navy,
        fontFamily: AIR_MONO, fontSize: 9, fontWeight: 700, cursor: "pointer",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        lineHeight: 1.05, flexShrink: 0, transition: "all 0.12s", padding: 0,
      }}
    >
      <span>{displayCode}</span>
      <span style={{ fontSize: 7, fontWeight: 600, opacity: 0.8 }}>
        {isFree ? "Free" : `₹${seat.Total_Amount}`}
      </span>
    </button>
  );
};

// ── Legend — same border/fill colors as the real seats, so it reads as
//    a key rather than decoration. ───────────────────────────────────
const LEGEND_ITEMS = [
  { label: "Selected", bg: C.sky, border: C.sky },
  { label: "Available", bg: "#fff", border: C.border },
  { label: "Extra charge", bg: "#fff", border: C.amber },
  { label: "Occupied", bg: "#E7EAF0", border: "#E7EAF0" },
  { label: "Taken by another passenger", bg: "#F1F5F9", border: C.border },
];
const SeatLegend = () => (
  <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 16, marginBottom: 14 }}>
    {LEGEND_ITEMS.map(({ label, bg, border }) => (
      <div key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 12, height: 12, borderRadius: "4px 4px 5px 5px", background: bg, border: `1.5px solid ${border}`, flexShrink: 0 }} />
        <span style={{ fontSize: 10.5, fontWeight: 600, color: C.muted }}>{label}</span>
      </div>
    ))}
  </div>
);

// ── Cabin artwork — the real airplane SVG, embedded (not stretched).
//    `preserveAspectRatio="xMidYMin slice"` scales it UNIFORMLY to cover
//    the box and crops overflow, anchored to the top — the nose is always
//    fully visible at a stable scale (driven by the box's width, which is
//    the binding dimension for our tall-narrow seat maps) no matter how
//    many rows follow, and only the tail gets cropped off on short
//    flights. A non-uniform "stretch to fit" (preserveAspectRatio="none")
//    is what caused an earlier version's nose zone to grow with row count
//    and swallow rows 1-2's text — slice mode can't do that: the artwork
//    never distorts, it only scales as a whole and crops.
//    Each instance gets its own gradient/filter ids via useId() since one
//    of these renders per passenger per leg — sharing ids across multiple
//    inline SVGs on the same page causes them to reference each other's
//    defs. ───────────────────────────────────────────────────────────
const PlaneArtwork = () => {
  const uid = useId();
  const bodyId = `air-plane-body-${uid}`;
  const wingId = `air-plane-wing-${uid}`;
  const shadowId = `air-plane-shadow-${uid}`;
  return (
    <svg
      viewBox="0 0 800 1800"
      preserveAspectRatio="xMidYMin slice"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 0 }}
    >
      <defs>
        <linearGradient id={bodyId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#d9dee7" />
          <stop offset="12%" stopColor="#ffffff" />
          <stop offset="50%" stopColor="#fafafa" />
          <stop offset="88%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#d9dee7" />
        </linearGradient>
        <linearGradient id={wingId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#f8f8f8" />
          <stop offset="100%" stopColor="#cfd6de" />
        </linearGradient>
        <filter id={shadowId} x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy="18" stdDeviation="25" floodOpacity="0.25" />
        </filter>
      </defs>
      <g filter={`url(#${shadowId})`}>
        <path
          fill={`url(#${bodyId})`}
          stroke="#cfd4dc"
          strokeWidth="2"
          d="M400 20 C520 20 615 115 640 235 L640 1540 C640 1675 540 1785 400 1785 C260 1785 160 1675 160 1540 L160 235 C185 115 280 20 400 20 Z"
        />
        <path fill="#edf1f7" d="M240 120 Q400 35 560 120 Q520 160 400 165 Q280 160 240 120Z" />
        <rect x="220" y="190" width="360" height="1400" rx="45" fill="#ffffff" />
        <path fill={`url(#${wingId})`} d="M160 690 L-120 830 L-60 1080 L160 1030Z" />
        <path fill={`url(#${wingId})`} d="M640 690 L920 830 L860 1080 L640 1030Z" />
        <ellipse cx="20" cy="890" rx="42" ry="115" fill="#d5dbe4" />
        <ellipse cx="780" cy="890" rx="42" ry="115" fill="#d5dbe4" />
        <path fill="#eef2f8" d="M305 1595 Q400 1715 495 1595Z" />
      </g>
    </svg>
  );
};

// ── One leg's seat map, laid out over the cabin shadow above. Aisle
//    position is derived from the actual column letters present in the
//    data (never hardcoded 3-3); any column that's part of this leg's
//    layout but missing from a specific row is rendered "Occupied" rather
//    than left blank — WeFlyGo only lists sellable seats per row, so a
//    present-elsewhere-but-missing-here column is a real signal, not a
//    guess. ────────────────────────────────────────────────────────
const Fuselage = ({ rows, paxId, groupKey, selected, takenSeatKeys, onSelect }) => {
  const { leftCols, rightCols } = useMemo(() => {
    const set = new Set();
    rows.forEach((row) => (row.Seat_Details || []).forEach((seat) => {
      // SSR_Status 0 = ISLE per WeFlyGo's docs — not a real seat, just an aisle-pathway
      // marker WeFlyGo includes in the row. Letting it through would add a phantom
      // "column" to the aisle-split guess and could render a bogus seat button for it.
      if (seat.SSR_Status === 0) return;
      const { col } = parseSeatCode(seat.SSR_TypeName || seat.SSR_TypeDesc);
      if (col) set.add(col);
    }));
    const cols = Array.from(set).sort();
    const mid = Math.ceil(cols.length / 2);
    return { leftCols: cols.slice(0, mid), rightCols: cols.slice(mid) };
  }, [rows]);

  if (!rows.length || (!leftCols.length && !rightCols.length)) {
    return <div style={{ fontSize: 12, color: C.label, fontStyle: "italic" }}>No seat map available for this flight.</div>;
  }

  const isLong = rows.length > 12;
  const labelStyle = { textAlign: "center", fontSize: 10, fontWeight: 700, color: C.label, letterSpacing: "0.14em" };

  return (
    <div style={{ maxHeight: isLong ? 500 : "none", overflowY: isLong ? "auto" : "visible" }}>
      <div style={{ position: "relative", width: "100%", maxWidth: "min(640px, 92%)", margin: "0 auto", padding: `150px 26px ${SEAT_SIZE}px`, boxSizing: "border-box" }}>
        <PlaneArtwork />
        <div style={{ position: "relative", zIndex: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{ ...labelStyle, marginBottom: 10 }}>Front</div>
          {rows.map((row, ri) => {
            const byCol = {};
            (row.Seat_Details || []).forEach((seat) => {
              if (seat.SSR_Status === 0) return; // ISLE marker, not a real seat
              const { col } = parseSeatCode(seat.SSR_TypeName || seat.SSR_TypeDesc);
              if (col) byCol[col] = seat;
            });
            const rowNum = parseSeatCode((row.Seat_Details || [])[0]?.SSR_TypeName || (row.Seat_Details || [])[0]?.SSR_TypeDesc).row;
            return (
              <div key={ri} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, marginBottom: 5 }}>
                <div style={{ width: 18, textAlign: "right", fontFamily: AIR_MONO, fontSize: 9, fontWeight: 700, color: C.label, flexShrink: 0 }}>
                  {rowNum}
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  {leftCols.map((c) => <SeatButton key={c} seat={byCol[c]} code={`${rowNum}${c}`} paxId={paxId} groupKey={groupKey} selected={selected} takenSeatKeys={takenSeatKeys} onSelect={onSelect} />)}
                </div>
                <div style={{ width: 22, borderTop: `1.5px dashed ${C.border}`, flexShrink: 0, alignSelf: "center" }} />
                <div style={{ display: "flex", gap: 4 }}>
                  {rightCols.map((c) => <SeatButton key={c} seat={byCol[c]} code={`${rowNum}${c}`} paxId={paxId} groupKey={groupKey} selected={selected} takenSeatKeys={takenSeatKeys} onSelect={onSelect} />)}
                </div>
              </div>
            );
          })}
          <div style={{ ...labelStyle, marginTop: 6 }}>Rear</div>
        </div>
      </div>
    </div>
  );
};

// Group a list of SSR items by SSR_TypeName and render one AddonGroup each —
// shared by both a specific leg's add-ons and the leftover "not tied to any
// leg" bucket below, so the grouping logic lives in one place.
// WeFlyGo can return what reads as the same option twice within one leg's
// own list (identical description + price, different SSR_Key) — collapse
// those content-identical entries to one chip rather than showing two
// indistinguishable choices for the same thing.
const renderAddonTypeGroups = (items, keyPrefix, selections, paxId, onSelect) => {
  const groups = {};
  const seenContent = new Set();
  items.forEach((s) => {
    const key = s.SSR_TypeName || "OTHER";
    const contentKey = `${key}|${s.SSR_TypeDesc || ""}|${s.Total_Amount || 0}`;
    if (seenContent.has(contentKey)) return;
    seenContent.add(contentKey);
    (groups[key] = groups[key] || []).push(s);
  });
  return Object.entries(groups).map(([groupName, groupItems]) => (
    <AddonGroup
      key={groupName}
      groupName={groupName}
      items={groupItems}
      groupKey={`addon_${keyPrefix}_${groupName}`}
      selections={selections}
      paxId={paxId}
      onSelect={onSelect}
    />
  ));
};

// ── One leg/segment — seat map AND that leg's own add-ons together,
//    collapsible as a single unit. A connecting itinerary's meals/etc.
//    repeat once per physical segment (WeFlyGo tags each with
//    Leg_Index/Segment_Id) — keeping them right under that leg's own seat
//    map instead of in a separate flat "Add-ons" section makes it obvious
//    which stop each add-on choice belongs to. Closed by default — the
//    passenger opens whichever leg they want to work on, rather than
//    every seat map/add-on picker being expanded up front. ────────────
const LegSection = ({ si, legIdx, fs, rows, paxId, groupKey, selected, takenSeatKeys, addonItems, selections, onSelect }) => {
  const [open, setOpen] = useState(false);
  const hasAddons = addonItems && addonItems.length > 0;
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, background: "#fff", border: "none", cursor: "pointer", padding: "8px 12px", textAlign: "left" }}
      >
        <span style={{ fontSize: 10, fontWeight: 700, color: C.label, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Leg {si + 1}
          {fs?.Origin && fs?.Destination && (
            <span style={{ color: C.navy, marginLeft: 6, textTransform: "none", letterSpacing: "normal" }}>{fs.Origin} → {fs.Destination}</span>
          )}
          {!open && selected && (
            <span style={{ color: C.sky, marginLeft: 6, textTransform: "none", letterSpacing: "normal", fontWeight: 700 }}>· Seat selected</span>
          )}
        </span>
        <span style={{
          width: 18, height: 18, borderRadius: 5, flexShrink: 0,
          background: "#F1F5F9", color: C.muted,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 12, fontWeight: 700, lineHeight: 1, transform: open ? "rotate(45deg)" : "none", transition: "transform 0.15s",
        }}>
          +
        </span>
      </button>
      {open && (
        <div style={{ padding: "6px 8px 12px" }}>
          <Fuselage rows={rows} paxId={paxId} groupKey={groupKey} selected={selected} takenSeatKeys={takenSeatKeys} onSelect={onSelect} />
          {hasAddons && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, display: "flex", alignItems: "center", gap: 5 }}>
                <IcBriefcase size={13} /> Add-ons for this leg
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {renderAddonTypeGroups(addonItems, `l${legIdx}_seg${si}`, selections, paxId, onSelect)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Seat map + add-ons for one leg + one passenger — one collapsible
//    LegSection per flight segment (connecting flights get one each),
//    each carrying its own seat map and its own leg's add-ons. Any add-on
//    that isn't tied to a specific leg (e.g. whole-trip baggage) falls
//    into a final "Other add-ons" bucket shown once, after all legs. ───
const SeatGrid = ({ legResult, flightSegments, ssrItems, paxType, paxId, legIdx, selections, takenSeatKeys, onSelect }) => {
  const segments = legResult?.Seat_Segments || [];
  if (!segments.length) {
    return <div style={{ fontSize: 12, color: C.label, fontStyle: "italic" }}>No seat map available for this flight.</div>;
  }

  // SSR_Type 3 = SEAT — Air_GetSSR's general ancillary list can include these alongside
  // meals/baggage/etc, but seat selection already has its own dedicated UI above (backed by
  // Air_GetSeatMap) — showing it again here would just be a second, confusing way to pick it.
  const applicableAddons = (ssrItems || []).filter(
    (s) => s.SSR_Type !== 3 && (!s.ApplicablePaxTypes || s.ApplicablePaxTypes.includes(paxType))
  );
  // Per WeFlyGo's own Air_GetSSR docs: Segment_Wise=true means the item is priced/sold per
  // physical segment (use Segment_Id/Leg_Index to know which); Segment_Wise=false means it's
  // "Journey Wise" — one whole-trip item (e.g. Frequent Flyer, Fastforward). Those never get
  // bucketed into a specific leg here — they're rendered once for the whole flight, shared
  // across passengers, by SharedAddonsSection at the leg level instead (see main render).
  const addonLegKey = (s) => {
    if (s.Segment_Wise !== true) return null;
    if (s.Segment_Id !== undefined && s.Segment_Id !== null) return String(s.Segment_Id);
    if (s.Leg_Index !== undefined && s.Leg_Index !== null) return String(s.Leg_Index);
    return null;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SeatLegend />
      {segments.map((seg, si) => {
        const fs = (flightSegments || [])[si];
        // Every selection key must encode BOTH which outer flight (legIdx — onward vs return
        // on a round trip) AND which physical segment within it (si — a connecting flight's
        // own stops) it belongs to. Missing legIdx here meant a round trip's return-flight
        // seat pick (both non-stop, so si was always 0 on both) silently overwrote the
        // onward flight's seat under the exact same "seat" key — the same class of bug
        // seat_0/seat_1 already fixed for connecting segments, just one level up.
        const seatGroupKey = segments.length > 1 ? `seat_l${legIdx}_s${si}` : `seat_l${legIdx}`;
        const legAddons = applicableAddons.filter((s) => addonLegKey(s) === String(si));
        return (
          <LegSection
            key={si}
            si={si}
            legIdx={legIdx}
            fs={fs}
            rows={seg.Seat_Row || []}
            paxId={paxId}
            groupKey={seatGroupKey}
            selected={selections?.[seatGroupKey]}
            takenSeatKeys={takenSeatKeys}
            addonItems={legAddons}
            selections={selections}
            onSelect={onSelect}
          />
        );
      })}
    </div>
  );
};

// ── Add-ons that aren't tied to a specific leg (Segment_Wise=false per
//    WeFlyGo's Air_GetSSR docs — e.g. Frequent Flyer, Fastforward). Shown
//    ONCE per flight here, not repeated inside every passenger's own
//    collapsible card, with each passenger's own pick broken out
//    underneath (they can still choose differently from each other). ──
const SharedAddonsSection = ({ ssrItems, paxList, legIdx, selections, onSelect }) => {
  const journeyWide = (ssrItems || []).filter((s) => s.SSR_Type !== 3 && s.Segment_Wise !== true);
  if (!journeyWide.length) return null;

  const typeGroups = {};
  journeyWide.forEach((s) => {
    const key = s.SSR_TypeName || "OTHER";
    (typeGroups[key] = typeGroups[key] || []).push(s);
  });

  return (
    <div style={{ background: C.card, borderRadius: 16, border: `1px solid ${C.border}`, boxShadow: "0 1px 4px rgba(0,0,0,0.04)", padding: 20, marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 12, display: "flex", alignItems: "center", gap: 5 }}>
        <IcBriefcase size={13} /> Other add-ons
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {Object.entries(typeGroups).map(([groupName, items]) => (
          <SharedAddonTypeGroup key={groupName} groupName={groupName} items={items} paxList={paxList} legIdx={legIdx} selections={selections} onSelect={onSelect} />
        ))}
      </div>
    </div>
  );
};

// One add-on CATEGORY, shown once (not once per passenger) — closed by
// default. Each passenger can still pick differently, so their own row of
// options only appears once you expand the category, instead of the whole
// category being repeated once per passenger up front.
const SharedAddonTypeGroup = ({ groupName, items, paxList, legIdx, selections, onSelect }) => {
  const [open, setOpen] = useState(false);
  // Journey-wide add-ons (Frequent Flyer, Fastforward) are fetched separately per outer flight
  // leg on a round trip (ssrByLeg[legIdx]) — without legIdx in the key, picking one for the
  // return flight would silently overwrite the onward flight's pick under the same key.
  const groupKey = `addon_shared_l${legIdx}_${groupName}`;

  // Same content-identical entries can repeat within one passenger's applicable list —
  // collapse those before splitting by passenger.
  const seenContent = new Set();
  const dedupedItems = items.filter((s) => {
    const contentKey = `${s.SSR_TypeDesc || ""}|${s.Total_Amount || 0}`;
    if (seenContent.has(contentKey)) return false;
    seenContent.add(contentKey);
    return true;
  });

  const paxOptions = paxList
    .map((pax) => ({
      pax,
      options: dedupedItems.filter((s) => !s.ApplicablePaxTypes || s.ApplicablePaxTypes.includes(pax.paxType)),
    }))
    .filter((p) => p.options.length > 0);

  const selectedCount = paxOptions.filter((p) => selections[p.pax.paxId]?.[groupKey]).length;

  return (
    <div style={{ border: `1.5px solid ${selectedCount > 0 ? C.sky : C.border}`, borderRadius: 10, overflow: "hidden" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, background: selectedCount > 0 ? "#EFF8FF" : "#fff", border: "none", cursor: "pointer", padding: "9px 12px", textAlign: "left" }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{groupName}</span>
          {selectedCount > 0 && (
            <span style={{ fontSize: 11, fontWeight: 700, color: C.sky }}>{selectedCount} selected</span>
          )}
        </span>
        <span style={{
          width: 20, height: 20, borderRadius: 6, flexShrink: 0,
          background: selectedCount > 0 ? C.sky : "#F1F5F9", color: selectedCount > 0 ? "#fff" : C.muted,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 14, fontWeight: 700, lineHeight: 1, transform: open ? "rotate(45deg)" : "none", transition: "transform 0.15s",
        }}>
          +
        </span>
      </button>

      {open && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "10px 12px 12px", borderTop: `1px solid ${C.border}` }}>
          {paxOptions.map(({ pax, options }) => (
            <div key={pax.paxId}>
              {paxList.length > 1 && (
                <div style={{ fontSize: 11, fontWeight: 700, color: C.navy, marginBottom: 6 }}>Passenger {pax.paxId}</div>
              )}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {options.map((item, i) => {
                  const isSelected = !!item.SSR_Key && selections[pax.paxId]?.[groupKey] === item.SSR_Key;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => onSelect(pax.paxId, groupKey, item.SSR_Key)}
                      style={{
                        display: "flex", alignItems: "center", gap: 6,
                        border: `1.5px solid ${isSelected ? C.sky : C.border}`,
                        background: isSelected ? "#EFF8FF" : "#fff",
                        color: isSelected ? C.sky : C.muted,
                        borderRadius: 10, padding: "7px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                      }}
                    >
                      {isSelected && <IcCheck size={11} />}
                      {item.SSR_TypeDesc || groupName}
                      <span style={{ fontFamily: AIR_MONO, fontWeight: 700, color: isSelected ? C.sky : C.navy }}>
                        {item.Total_Amount ? `· ₹${item.Total_Amount}` : "· Free"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── One add-on category — closed by default, tap "+" to open and pick.
//    Whatever's currently selected shows on its own line BELOW the group
//    header (not crammed into the same row as the name), so it stays
//    visible even while the picker itself is collapsed. ─────────────
const AddonGroup = ({ groupName, items, groupKey, selections, paxId, onSelect }) => {
  const [open, setOpen] = useState(false);
  const selectedItem = items.find((item) => !!item.SSR_Key && selections?.[groupKey] === item.SSR_Key);

  return (
    <div style={{ border: `1.5px solid ${selectedItem ? C.sky : C.border}`, borderRadius: 10, overflow: "hidden" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%", background: selectedItem ? "#EFF8FF" : "#fff", border: "none", cursor: "pointer",
          padding: "9px 12px", textAlign: "left",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {groupName}
          </span>
          <span style={{
            width: 20, height: 20, borderRadius: 6, flexShrink: 0,
            background: selectedItem ? C.sky : "#F1F5F9", color: selectedItem ? "#fff" : C.muted,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 14, fontWeight: 700, lineHeight: 1, transform: open ? "rotate(45deg)" : "none", transition: "transform 0.15s",
          }}>
            +
          </span>
        </div>
        {selectedItem && (
          <div style={{ marginTop: 4, fontSize: 12, fontWeight: 600, color: C.sky }}>
            {selectedItem.SSR_TypeDesc || groupName}
            <span style={{ fontFamily: AIR_MONO, fontWeight: 700 }}>
              {selectedItem.Total_Amount ? ` · ₹${selectedItem.Total_Amount}` : " · Free"}
            </span>
          </div>
        )}
      </button>

      {open && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "10px 12px 12px", borderTop: `1px solid ${C.border}` }}>
          {items.map((item, i) => {
            const isSelected = !!item.SSR_Key && selections?.[groupKey] === item.SSR_Key;
            return (
              <button
                key={i}
                type="button"
                onClick={() => onSelect(paxId, groupKey, item.SSR_Key)}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  border: `1.5px solid ${isSelected ? C.sky : C.border}`,
                  background: isSelected ? "#EFF8FF" : "#fff",
                  color: isSelected ? C.sky : C.muted,
                  borderRadius: 10, padding: "7px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                }}
              >
                {isSelected && <IcCheck size={11} />}
                {item.SSR_TypeDesc || groupName}
                <span style={{ fontFamily: AIR_MONO, fontWeight: 700, color: isSelected ? C.sky : C.navy }}>
                  {item.Total_Amount ? `· ₹${item.Total_Amount}` : "· Free"}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Passenger row: seat + that seat's own add-ons, per leg. Collapsible
//    per passenger — closed by default, so with 3-4 passengers the page
//    opens as a compact list of names rather than every seat map/add-on
//    picker expanded up front; the passenger opens whichever one they
//    want to work on. ─────────────────────────────────────────────────
const PaxAddonCard = ({ pax, legResult, flightSegments, ssrItems, legIdx, selections, takenSeatKeys, onSelect }) => {
  const [open, setOpen] = useState(false);
  const seatCount = Object.keys(selections || {}).filter((k) => k === "seat" || k.startsWith("seat_")).length;
  const addonCount = Object.keys(selections || {}).filter((k) => k.startsWith("addon_")).length;

  return (
    <div style={{ background: "#FAFCFF", borderRadius: 12, border: `1px solid ${C.border}`, marginBottom: 10, overflow: "hidden" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", padding: 16, textAlign: "left" }}
      >
        <div style={{ width: 26, height: 26, borderRadius: 8, background: C.navy, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", flexShrink: 0 }}>
          <IcUser size={13} />
        </div>
        <span style={{ fontWeight: 700, fontSize: 12.5, color: C.navy }}>Passenger {pax.paxId}</span>
        <span style={{ background: "#EFF8FF", color: C.sky, fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 6 }}>
          {pax.label.toUpperCase()}
        </span>
        {!open && (seatCount > 0 || addonCount > 0) && (
          <span style={{ fontSize: 11, color: C.muted }}>
            {seatCount > 0 ? `${seatCount} seat${seatCount > 1 ? "s" : ""}` : ""}
            {seatCount > 0 && addonCount > 0 ? " · " : ""}
            {addonCount > 0 ? `${addonCount} add-on${addonCount > 1 ? "s" : ""}` : ""}
          </span>
        )}
        <span style={{
          marginLeft: "auto", width: 20, height: 20, borderRadius: 6, flexShrink: 0,
          background: "#F1F5F9", color: C.muted,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 14, fontWeight: 700, lineHeight: 1, transform: open ? "rotate(45deg)" : "none", transition: "transform 0.15s",
        }}>
          +
        </span>
      </button>

      {open && (
        <div style={{ padding: "0 16px 16px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 8, display: "flex", alignItems: "center", gap: 5 }}>
            <IcArmchair size={13} /> Seats &amp; add-ons per leg
          </div>
          <SeatGrid
            legResult={legResult}
            flightSegments={flightSegments}
            ssrItems={ssrItems}
            paxType={pax.paxType}
            paxId={pax.paxId}
            legIdx={legIdx}
            selections={selections}
            takenSeatKeys={takenSeatKeys}
            onSelect={onSelect}
          />
        </div>
      )}
    </div>
  );
};

const TipsBanner = () => (
  <div style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "#EFF8FF", border: "1px solid #D6ECFB", borderRadius: 12, padding: "12px 14px", marginTop: 4, color: C.navy }}>
    <IcInfo size={15} />
    <span style={{ fontSize: 12, lineHeight: 1.5 }}>
      Selected seats and add-ons are added to the fare shown in Fare Summary and confirmed once your booking is complete.
    </span>
  </div>
);

// ── Sidebar: every selected seat/add-on across every passenger and leg,
//    with a running total and the primary CTA — so the choice made deep
//    in a long seat map is always visible without scrolling back up. ──
const SelectionSidebar = ({ paxList, selections, metaByKey, selectedTotal, onGoNext }) => {
  const items = [];
  paxList.forEach((pax) => {
    const paxSel = selections[pax.paxId] || {};
    Object.entries(paxSel).forEach(([groupKey, ssrKey]) => {
      const info = metaByKey[ssrKey];
      if (ssrKey && info) items.push({ pax, groupKey, info });
    });
  });

  return (
    <div style={{ background: C.card, borderRadius: 16, border: `1px solid ${C.border}`, boxShadow: "0 1px 4px rgba(0,0,0,0.04)", padding: 18 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: C.navy }}>Your Selection</div>
        <span style={{ background: "#EFF8FF", color: C.sky, fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 6 }}>
          {items.length} item{items.length === 1 ? "" : "s"}
        </span>
      </div>

      {items.length === 0 ? (
        <div style={{ fontSize: 12, color: C.label, fontStyle: "italic", marginBottom: 14 }}>
          No seats or add-ons selected yet.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
          {items.map((item, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <Readout size={13} weight={700} color={C.navy}>{item.info.ssrTypeDesc || item.info.ssrTypeName}</Readout>
                <div style={{ fontSize: 11, color: C.label, marginTop: 1 }}>
                  {paxList.length > 1 ? `Passenger ${item.pax.paxId} · ` : ""}{item.groupKey.startsWith("seat") ? "Seat" : "Add-on"}
                </div>
              </div>
              <Readout size={13} weight={700} color={C.navy} style={{ flexShrink: 0 }}>
                {item.info.amount ? `₹${item.info.amount}` : "Free"}
              </Readout>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 12, borderTop: `1px solid ${C.border}`, marginBottom: 14 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.muted }}>Total</span>
        <Readout size={18} weight={700} color={C.navy}>₹{selectedTotal.toLocaleString("en-IN")}</Readout>
      </div>

      <button
        onClick={onGoNext}
        style={{ width: "100%", background: C.navy, color: "#fff", border: "none", borderRadius: 12, padding: "12px 0", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
      >
        Continue
      </button>
      <button
        onClick={onGoNext}
        style={{ width: "100%", background: "none", color: C.muted, border: "none", padding: "10px 0 0", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}
      >
        Skip for now
      </button>
    </div>
  );
};

// ── Fare Summary — Fare (already tax-inclusive per WeFlyGo's FareDetails)
//    + seat/add-on charges + total. No fabricated base-fare/tax split —
//    that granularity isn't available in the data this page has. ─────
const FareSummaryCard = ({ fareAmount, addonsTotal, refundable }) => (
  <div style={{ background: C.card, borderRadius: 16, border: `1px solid ${C.border}`, boxShadow: "0 1px 4px rgba(0,0,0,0.04)", padding: 18, marginTop: 14 }}>
    <div style={{ fontWeight: 700, fontSize: 14, color: C.navy, marginBottom: 12 }}>Fare Summary</div>
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, color: C.muted }}>
        <span>Fare</span>
        <Readout size={12.5} weight={600} color={C.navy}>₹{fareAmount.toLocaleString("en-IN")}</Readout>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, color: C.muted }}>
        <span>Seats &amp; add-ons</span>
        <Readout size={12.5} weight={600} color={C.navy}>₹{addonsTotal.toLocaleString("en-IN")}</Readout>
      </div>
    </div>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>Total Amount</span>
      <Readout size={16} weight={700} color={C.navy}>₹{(fareAmount + addonsTotal).toLocaleString("en-IN")}</Readout>
    </div>
    {refundable !== null && (
      <div style={{ marginTop: 12 }}>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20, letterSpacing: "0.04em",
          background: refundable ? "#ECFDF5" : "#F1F5F9", color: refundable ? "#059669" : C.muted,
        }}>
          {refundable ? "REFUNDABLE" : "NON-REFUNDABLE"}
        </span>
      </div>
    )}
  </div>
);

// ── Main component ───────────────────────────────────────────────────
const FlightSSRSeat = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    searchKey, repricedFlights = [], requiredPaxDetails = [],
    paxCounts = {}, tripType = 0, fareChanged = false, searchForm = {},
    provider = "weflygo", nextraBookingKey = null, nextraTotalPrice = null, nextraDomInt = null,
  } = location.state || {};

  const { callApi: ssrApi } = useApi("/air/get-ssr", "POST");
  const { callApi: seatMapApi } = useApi("/air/seat-map", "POST");
  const { callApi: nextraSsrApi } = useApi("/air/nextra/get-ssr", "POST");
  const { callApi: nextraSeatMapApi } = useApi("/air/nextra/seat-map", "POST");

  const [loading, setLoading] = useState(true);
  const [ssrByLeg, setSsrByLeg] = useState([]);      // [{ ssrDetails: [...] }, ...] per leg
  const [seatByLeg, setSeatByLeg] = useState([]);    // [{ seatSegments: [...] }, ...] per leg
  // selections[paxId] = { seat: ssrKey, [addon_TYPE]: ssrKey, ... }
  const [selections, setSelections] = useState({});

  const paxList = useMemo(() => buildPaxList(paxCounts), [paxCounts]);
  const flightKeys = useMemo(() => repricedFlights.map((f) => f.Flight_Key), [repricedFlights]);

  useEffect(() => {
    if (!flightKeys.length) { setLoading(false); return; }

    if (provider === "nextra") {
      if (!nextraBookingKey) { setLoading(false); return; }
      (async () => {
        // One /get-ssr + /seat-map call per outer leg (onward, and return for a round trip) —
        // tripType picks which leg Nextra returns data for, same bookingkey covers both.
        const legCalls = repricedFlights.map((f, legIdx) => {
          const jtype = legIdx === 0 ? "onward" : "return";
          return Promise.allSettled([
            nextraSsrApi({ selectedFlight: nextraBookingKey, tripType: jtype, wsMode: "test" }),
            nextraSeatMapApi({ selectedFlight: nextraBookingKey, tripType: jtype, wsMode: "test" }),
          ]).then(([ssrRes, seatRes]) => ({
            ssr: ssrRes.status === "fulfilled" ? normalizeNextraSSR(ssrRes.value, f.Segments, jtype) : [],
            seat: seatRes.status === "fulfilled" ? normalizeNextraSeatMap(seatRes.value, jtype) : { Seat_Segments: [] },
          }));
        });
        const results = await Promise.all(legCalls);
        setSsrByLeg(results.map((r) => r.ssr));
        setSeatByLeg(results.map((r) => r.seat));
        setLoading(false);
      })();
      return;
    }

    // All optional PAX fields sent as null, matching WeFlyGo's own documented Air_GetSeatMap
    // example exactly — this step runs before the booking form collects real passenger details
    // (passport, DOB, frequent flyer, etc.), so there's genuinely no value yet for any of them.
    const placeholderPax = paxList.map((p) => ({
      paxId: p.paxId, paxType: p.paxType,
      title: p.paxType === 0 ? "MR" : "MSTR",
      firstName: "Passenger", lastName: String(p.paxId),
      gender: 0, age: null, dob: null,
      passportNumber: null, passportIssuingCountry: null, passportExpiry: null,
      nationality: null, frequentFlyerDetails: null,
    }));

    (async () => {
      const [ssrRes, seatRes] = await Promise.allSettled([
        ssrApi({ searchKey, flightKeys }),
        seatMapApi({ searchKey, flightKeys, paxDetails: placeholderPax }),
      ]);

      if (ssrRes.status === "fulfilled" && ssrRes.value?.SSRFlightDetails) {
        setSsrByLeg(ssrRes.value.SSRFlightDetails.map((f) => f.SSRDetails || []));
      }
      if (seatRes.status === "fulfilled" && seatRes.value?.AirSeatMaps) {
        setSeatByLeg(seatRes.value.AirSeatMaps);
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, nextraBookingKey, searchKey, flightKeys.join(",")]);

  const handleSelect = (paxId, groupKey, ssrKey) => {
    setSelections((prev) => {
      const paxSel = { ...(prev[paxId] || {}) };
      if (paxSel[groupKey] === ssrKey) {
        delete paxSel[groupKey]; // toggle off
      } else {
        paxSel[groupKey] = ssrKey;
      }
      return { ...prev, [paxId]: paxSel };
    });
  };

  // Every groupKey is stamped with which outer leg (onward vs return) it was picked for —
  // "seat_l0_s0"/"seat_l1", "addon_l0_seg0_MEAL", "addon_shared_l1_Baggage" — so the leg index
  // can be recovered from the key itself. This MUST stay split per leg: flattening every leg's
  // picks into one shared list (the previous behaviour) meant FlightBooking.js had no way to
  // tell them apart and copied that same combined list onto every segment's BookingSSRDetails —
  // which is exactly the "same SSR key on both outbound and inbound" bug WeFlyGo rejected
  // bookings for (Air_Ticketing: "SSR (FRCK) is restricted to 1 occurence(s) per passenger and
  // leg"), and silently over-counted the add-on cost that got billed at AddPayment time.
  const legIndexFromGroupKey = (groupKey) => {
    const m = /^(?:seat_l|addon_l|addon_shared_l)(\d+)/.exec(groupKey);
    return m ? Number(m[1]) : 0;
  };

  const buildBookingSSRDetails = () => {
    const byLeg = repricedFlights.map(() => []);
    Object.entries(selections).forEach(([paxId, groups]) => {
      Object.entries(groups).forEach(([groupKey, ssrKey]) => {
        if (!ssrKey) return;
        const legIdx = legIndexFromGroupKey(groupKey);
        (byLeg[legIdx] || byLeg[0]).push({ paxId: Number(paxId), ssrKey });
      });
    });
    return byLeg;
  };

  // DB-only metadata (not sent to WeFlyGo) — same selections, enriched with
  // description + amount so bookings show what add-ons were bought without
  // needing a live Air_Reprint call. SSR_Key itself isn't kept — like
  // flight_key/search_key, it's an opaque token scoped to this search/pricing
  // session and is meaningless once the booking is confirmed.
  const buildAddonsMeta = () => {
    const out = [];
    Object.entries(selections).forEach(([paxId, groups]) => {
      Object.values(groups).forEach((ssrKey) => {
        const info = metaByKey[ssrKey];
        if (ssrKey && info) out.push({ pax_id: Number(paxId), ssr_type_name: info.ssrTypeName, ssr_type_desc: info.ssrTypeDesc, amount: info.amount });
      });
    });
    return out;
  };

  // SSR_Key -> Total_Amount, so the selection total can be computed without
  // re-fetching — both GetSSR items and seat-map items share this shape.
  const priceByKey = useMemo(() => {
    const map = {};
    ssrByLeg.forEach((items) => (items || []).forEach((it) => {
      if (it.SSR_Key) map[it.SSR_Key] = it.Total_Amount || 0;
    }));
    seatByLeg.forEach((leg) => (leg?.Seat_Segments || []).forEach((seg) =>
      (seg.Seat_Row || []).forEach((row) => (row.Seat_Details || []).forEach((seat) => {
        if (seat.SSR_Key) map[seat.SSR_Key] = seat.Total_Amount || 0;
      }))
    ));
    return map;
  }, [ssrByLeg, seatByLeg]);

  // SSR_Key -> descriptive info, so the selection can be saved to our own DB
  // (WeFlyGo's booking payload only ever gets paxId+ssrKey, never the description).
  const metaByKey = useMemo(() => {
    const map = {};
    ssrByLeg.forEach((items) => (items || []).forEach((it) => {
      if (it.SSR_Key) map[it.SSR_Key] = { ssrTypeName: it.SSR_TypeName, ssrTypeDesc: it.SSR_TypeDesc, amount: it.Total_Amount || 0 };
    }));
    seatByLeg.forEach((leg) => (leg?.Seat_Segments || []).forEach((seg) =>
      (seg.Seat_Row || []).forEach((row) => (row.Seat_Details || []).forEach((seat) => {
        if (seat.SSR_Key) map[seat.SSR_Key] = { ssrTypeName: seat.SSR_TypeName || "SEAT", ssrTypeDesc: seat.SSR_TypeDesc || seat.SSR_Code || "Seat", amount: seat.Total_Amount || 0 };
      }))
    ));
    return map;
  }, [ssrByLeg, seatByLeg]);

  const selectedTotal = useMemo(() => {
    let sum = 0;
    Object.values(selections).forEach((groups) => {
      Object.values(groups).forEach((ssrKey) => { sum += priceByKey[ssrKey] || 0; });
    });
    return sum;
  }, [selections, priceByKey]);

  // SSR_Key -> Nextra's own raw item fields (ssrid/ssrcode/ssrtype/carrier/flightNumber/
  // origin/destination/jtype) — not needed by any of the render components above (which only
  // ever look at the WeFlyGo-shaped fields), but FlightConfirm.js needs these to build the real
  // NextraSSRRow[] for Nextra's /book call once passenger names are known.
  const nextraRawByKey = useMemo(() => {
    if (provider !== "nextra") return {};
    const map = {};
    ssrByLeg.forEach((items) => (items || []).forEach((it) => {
      if (it.SSR_Key && it._nextraRaw) map[it.SSR_Key] = it._nextraRaw;
    }));
    seatByLeg.forEach((leg) => (leg?.Seat_Segments || []).forEach((seg) =>
      (seg.Seat_Row || []).forEach((row) => (row.Seat_Details || []).forEach((seat) => {
        if (seat.SSR_Key && seat._nextraRaw) map[seat.SSR_Key] = seat._nextraRaw;
      }))
    ));
    return map;
  }, [provider, ssrByLeg, seatByLeg]);

  // Nextra-only counterpart to buildBookingSSRDetails/buildAddonsMeta above — paxId plus every
  // raw field NextraSSRRow needs except the passenger's name (added later in FlightConfirm.js,
  // once FlightBooking.js has actually collected it).
  const buildNextraSelectedAddons = () => {
    const out = [];
    Object.entries(selections).forEach(([paxId, groups]) => {
      Object.values(groups).forEach((ssrKey) => {
        const raw = nextraRawByKey[ssrKey];
        if (ssrKey && raw) out.push({ paxId: Number(paxId), ...raw });
      });
    });
    return out;
  };

  // Real fare (already tax-inclusive) and refundability, straight from the same Fares[0]
  // block FlightBooking.js uses to build the actual booking.
  // WeFlyGo's FareDetails is PER PAX TYPE (PAX_Type: 0=Adult, 1=Child, 2=Infant per AirFareDetail
  // .java) — each entry is that type's own Total_Amount, not a flat per-pax rate shared by
  // everyone. Summing every entry and multiplying by total headcount (the old formula) double-
  // (or worse-) counts as soon as pax types are mixed: 2 adults + 2 children at different fares
  // A and C should total 2A + 2C, not (A+C)*4. Each entry must be multiplied by ONLY its own
  // type's count instead. Nextra's flightfare.totalnet, in contrast, is already the GRAND total
  // for every passenger in the search — nextraTotalPrice (from the reprice call) is used as-is.
  const fareAmount = useMemo(() => {
    if (provider === "nextra") return nextraTotalPrice || 0;
    const countByType = [paxCounts.adults || 0, paxCounts.children || 0, paxCounts.infants || 0];
    return repricedFlights.reduce((sum, f) => {
      const fareDetails = f.Fares?.[0]?.FareDetails || [];
      return sum + fareDetails.reduce((s, fd) => s + (fd.Total_Amount || 0) * (countByType[fd.PAX_Type] ?? 0), 0);
    }, 0);
  }, [provider, nextraTotalPrice, repricedFlights, paxCounts]);
  const refundable = repricedFlights[0]?.Fares?.[0]?.Refundable ?? null;

  const goNext = () => {
    navigate("/flight-booking", {
      state: {
        provider, searchKey, repricedFlights, requiredPaxDetails, paxCounts,
        tripType, fareChanged, searchForm,
        bookingSSRDetails: buildBookingSSRDetails(),
        addonsTotal: selectedTotal,
        addonsMeta: buildAddonsMeta(),
        nextraBookingKey, nextraTotalPrice, nextraDomInt,
        nextraSelectedAddons: provider === "nextra" ? buildNextraSelectedAddons() : [],
      },
    });
  };

  // Empty state — no flights selected, bounce back
  if (!repricedFlights.length) {
    return (
      <div style={{ minHeight: "60vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, background: C.bg, fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,sans-serif" }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.navy }}>No flight selected</div>
        <div style={{ fontSize: 13, color: C.muted }}>Go back and search for a flight first.</div>
        <button onClick={() => navigate("/flight-search")} style={{ background: C.navy, color: "#fff", border: "none", borderRadius: 10, padding: "10px 22px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          Search flights
        </button>
      </div>
    );
  }

  return (
    <div style={{ background: C.bg, minHeight: "100vh", fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,sans-serif" }}>
      <AirFontImport />
      <style>{`
        .air-ssr-layout { display: flex; align-items: flex-start; gap: 20px; }
        .air-ssr-main { flex: 1; min-width: 0; }
        .air-ssr-sidebar { width: 340px; flex-shrink: 0; position: sticky; top: 76px; }
        @media (max-width: 900px) {
          .air-ssr-layout { flex-direction: column; }
          .air-ssr-sidebar { width: 100%; position: static; }
        }
        @media (max-width: 640px) {
          /* Guaranteed-correct stack instead of relying on flex-wrap, which was wrapping
             airline info and route times onto separate lines in an order that read wrong. */
          .air-flight-info-row { flex-direction: column; align-items: stretch !important; gap: 14px; }
          .air-flight-info-airline { width: 100%; }
          .air-flight-info-route { width: 100%; justify-content: space-between !important; gap: 8px !important; }

          /* MiniLayout (the shared app shell) already pads every page on mobile — this page's
             own padding was stacking on top of that. Can't touch the shell from here. */
          .air-ssr-page { padding-left: 4px !important; padding-right: 4px !important; }
        }
      `}</style>

      {/* ── Sticky header ── */}
      <div style={{ background: "#fff", borderBottom: `1px solid ${C.border}`, padding: "10px 20px", display: "flex", alignItems: "center", gap: 14, position: "sticky", top: 0, zIndex: 20 }}>
        <button onClick={() => navigate(-1)} style={{ width: 36, height: 36, borderRadius: 10, border: `1.5px solid ${C.border}`, background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, flexShrink: 0 }}>
          <IcArrowLeft size={18} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.navy, lineHeight: 1.2 }}>Seat Selection</div>
          <div style={{ fontSize: 11, color: C.label, marginTop: 2 }}>
            {searchForm.from} → {searchForm.to} · {tripType === 1 ? "Round Trip" : "One Way"} · Optional — you can skip this step
          </div>
        </div>
      </div>

      {/* ── Page body ── */}
      <div className="air-ssr-page" style={{ maxWidth: 1180, margin: "0 auto", padding: "20px 16px 40px" }}>
        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 0", color: C.muted, fontSize: 13 }}>
            Loading seats &amp; add-ons…
          </div>
        ) : (
          <div className="air-ssr-layout">
            <div className="air-ssr-main">
              {!ssrByLeg.some((l) => l.length) && !seatByLeg.some((l) => l?.Seat_Segments?.length) ? (
                <div style={{ background: C.card, borderRadius: 16, border: `1px solid ${C.border}`, padding: "24px 20px", display: "flex", gap: 10, alignItems: "flex-start", color: C.muted, marginBottom: 20 }}>
                  <IcInfo size={16} />
                  <span style={{ fontSize: 13 }}>No seats or add-ons available for this itinerary. You can continue to passenger details.</span>
                </div>
              ) : (
                repricedFlights.map((f, legIdx) => (
                  <div key={legIdx} style={{ marginBottom: 20 }}>
                    <FlightInfoCard f={f} legLabel={tripType === 1 ? (legIdx === 0 ? "Onward" : "Return") : "Flight"} />
                    <div style={{ background: C.card, borderRadius: 16, border: `1px solid ${C.border}`, boxShadow: "0 1px 4px rgba(0,0,0,0.04)", padding: 20 }}>
                      {paxList.map((pax) => {
                        // Seats already picked by the OTHER passengers on this booking — blocks
                        // two passengers from ending up assigned to the same physical seat.
                        const takenSeatKeys = new Set(
                          paxList
                            .filter((p) => p.paxId !== pax.paxId)
                            .flatMap((p) => Object.entries(selections[p.paxId] || {})
                              .filter(([gk]) => gk === "seat" || gk.startsWith("seat_"))
                              .map(([, ssrKey]) => ssrKey))
                            .filter(Boolean)
                        );
                        return (
                          <PaxAddonCard
                            key={pax.paxId}
                            pax={pax}
                            legResult={seatByLeg[legIdx]}
                            flightSegments={f.Segments}
                            ssrItems={ssrByLeg[legIdx]}
                            legIdx={legIdx}
                            selections={selections[pax.paxId]}
                            takenSeatKeys={takenSeatKeys}
                            onSelect={handleSelect}
                          />
                        );
                      })}
                      <TipsBanner />
                    </div>
                    <SharedAddonsSection ssrItems={ssrByLeg[legIdx]} paxList={paxList} legIdx={legIdx} selections={selections} onSelect={handleSelect} />
                  </div>
                ))
              )}
            </div>

            <aside className="air-ssr-sidebar">
              <SelectionSidebar paxList={paxList} selections={selections} metaByKey={metaByKey} selectedTotal={selectedTotal} onGoNext={goNext} />
              <FareSummaryCard fareAmount={fareAmount} addonsTotal={selectedTotal} refundable={refundable} />
            </aside>
          </div>
        )}
      </div>
    </div>
  );
};

export default FlightSSRSeat;